import { useState, useEffect } from 'react';
import { BASE_URL, SCORECARD_KEY } from '@/constants';

const SEARCH_DEBOUNCE_MS = 300;
const MAX_RESULTS = 50;

const SCORECARD_URL = 'https://api.data.gov/ed/collegescorecard/v1/schools';

export const useCollegeSearch = (query: string) => {
	const [results, setResults] = useState<string[]>([]);
	const [isLoading, setIsLoading] = useState(false);

	useEffect(() => {
		const trimmed = query.trim();

		if (!trimmed) {
			setResults([]);
			return;
		}

		const isLongEnough = trimmed.length >= 3;

		const controller = new AbortController();

		const timer = setTimeout(async () => {
			setIsLoading(true);

			try {
				const [hipoRes, scorecardRes] = await Promise.allSettled([
					fetch(`${BASE_URL}/universities/search?name=${encodeURIComponent(trimmed)}`, {
						signal: controller.signal,
					}),
					isLongEnough
						? fetch(
								`${SCORECARD_URL}?api_key=${SCORECARD_KEY}&school.name=${encodeURIComponent(trimmed)}&fields=school.name`,
								{ signal: controller.signal }
							)
						: Promise.reject(new Error('query too short')),
				]);

				const names: string[] = [];

				if (hipoRes.status === 'fulfilled' && hipoRes.value.ok) {
					const data = await hipoRes.value.json();

					data.forEach((item: { name: string }) => {
						names.push(item.name);
					});
				}

				if (scorecardRes.status === 'fulfilled' && scorecardRes.value.ok) {
					const data = await scorecardRes.value.json();

					data.results?.forEach((item: { 'school.name': string }) => {
						names.push(item['school.name']);
					});
				}

				const seen = new Set<string>();

				const deduped = names.filter((name) => {
					const key = name.toLowerCase();

					if (seen.has(key)) {
						return false;
					}

					seen.add(key);

					return true;
				});

				setResults(deduped.slice(0, MAX_RESULTS));
			} finally {
				setIsLoading(false);
			}
		}, SEARCH_DEBOUNCE_MS);

		return () => {
			clearTimeout(timer);
			controller.abort();
		};
	}, [query]);

	return { results, isLoading };
};
